import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useKeenSlider } from 'keen-slider/react';
import { Player, ControlBar, BigPlayButton } from 'video-react';
import { isEmpty } from 'lodash';
import { DamageTypesName } from 'constants/DamageTypes';
import { prepareDamagesDescription } from '../helpers/prepareDamagesDescription';
import warningIcon from '../../assets/img/warningIcon.png';
import 'keen-slider/keen-slider.min.css';
import { UploadFileTypes } from '../../constants/UploadFileTypes';
import noImageImg from '../../assets/img/noimage.svg';
import { sortPhotosByPerspective } from '../../helpers/sortPhotosByPerspective';

const VehicleImagesPopupSlider = ({
  photos,
  videos,
  initial,
  onDamageClick,
  isDamageSlider,
  showImageTitle,
}) => {
  const [currentSlide, setCurrentSlide] = useState(initial);
  const [hidden, setHidden] = useState(true);
  const [sliderRef, slider] = useKeenSlider({
    initial,
    spacing: 5,
    slideChanged(s) {
      setCurrentSlide(s.details().relativeSlide);
    },
    mounted: () => {
      setHidden(false);
    },
  });
  const [secondSliderRef, secondSlider] = useKeenSlider({
    initial,
    slidesPerView: 6,
  });

  const sortedPhotos = !isDamageSlider ? sortPhotosByPerspective(photos) : photos;

  return (
    <div className="popup__slider-wrapper pt-0">
      <div ref={sliderRef} className="keen-slider popup__slider">
        {sortedPhotos.length > 0 &&
          sortedPhotos.map(item =>
            item?.fileUrl ? (
              <div
                key={item.id}
                className={`keen-slider__slide popup__slide-container ${
                  hidden ? 'hidden' : ''
                }`}
              >
                <div className="popup__slide">
                  {item?.damageType && showImageTitle && (
                    <span className="popup__damage-type">
                      {DamageTypesName[item.damageType]}
                    </span>
                  )}
                  <img
                    src={item.fileUrl}
                    alt="Auction vehicle"
                    className="popup__slide-image"
                    onError={e => {
                      e.target.src = noImageImg;
                    }}
                  />
                  {!isEmpty(item?.damages) && (
                    <button
                      type="button"
                      className="photo_gallery_damage_button"
                      onClick={() => onDamageClick(item.damages, item.perspective)}
                    >
                      <img src={warningIcon} alt="Warning icon" />
                      <span className="popup__damage-counter">{item.damages.length}</span>
                      <span>{prepareDamagesDescription(item.damages)}</span>
                    </button>
                  )}
                </div>
              </div>
            ) : null,
          )}
        {videos.length > 0 &&
          videos.map(item => (
            <div
              key={item.id}
              className={`keen-slider__slide popup__slide-container ${hidden ? 'hidden' : ''}`}
            >
              <Player src={item.fileUrl} className="video-react_popup_slider">
                <BigPlayButton position="center" />
                <ControlBar autoHide={false} className="my-class" />

                {!isEmpty(item?.damages) && (
                  <button
                    type="button"
                    className="photo_gallery_damage_button"
                    onClick={() => onDamageClick(item.damages)}
                  >
                    <img src={warningIcon} alt="Warning icon" />
                    <span className="popup__damage-counter">
                      {item.damages && item.damages?.length}
                    </span>
                  </button>
                )}
              </Player>
              {item?.damageType && (
                <span className="popup__damage-type">{DamageTypesName[item.damageType]}</span>
              )}
            </div>
          ))}
        {slider && (
          <>
            {currentSlide > 0 && (
              <button
                type="button"
                onClick={e => e.stopPropagation() || slider.prev() || secondSlider.prev()}
                className="popup__slider-arrow-button popup__slider-arrow-button--left"
              >
                <i className="fas fa-chevron-left" />
              </button>
            )}
            {sortedPhotos.concat(videos)?.length &&
              currentSlide < sortedPhotos.concat(videos).length - 1 && (
                <button
                  type="button"
                  onClick={e => e.stopPropagation() || slider.next() || secondSlider.next()}
                  className="popup__slider-arrow-button popup__slider-arrow-button--right"
                >
                  <i className="fas fa-chevron-right" />
                </button>
              )}
          </>
        )}
      </div>
      <div
        ref={secondSliderRef}
        className={`keen-slider popup__thumbnails-slider ${
          (sortedPhotos?.length || videos?.length) && sortedPhotos?.length + videos?.length < 5
            ? `popup__thumbnails-slider-${sortedPhotos.length + videos?.length}`
            : ''
        }`}
      >
        {sortedPhotos.concat(videos).length &&
          sortedPhotos.concat(videos).map((item, idx) => (
            <aside key={item.id} className="keen-slider__slide popup-slider__slide">
              <button
                className="popup__slide-button"
                type="button"
                onClick={() => {
                  slider.moveToSlideRelative(idx, true);
                  secondSlider.moveToSlideRelative(idx, true);
                }}
              >
                <aside
                  className={`popup__thumbnails-slide ${
                    idx === currentSlide ? 'popup__slide-active' : ''
                  }`}
                >
                  <img
                    className="popup__thumbnails-slide-image"
                    src={
                      item?.type === UploadFileTypes.VIDEO_TYPE
                        ? item.previewUrl
                        : item.fileUrl
                    }
                    onError={e => {
                      e.target.src = noImageImg;
                    }}
                    alt="Auction vehicle thumbnail"
                  />
                </aside>
              </button>
            </aside>
          ))}
        {secondSlider && (
          <>
            {currentSlide > 0 && (
              <button
                type="button"
                onClick={e => e.stopPropagation() || secondSlider.prev() || slider.prev()}
                className="popup__slider-arrow-button popup__slider-arrow-button--left"
              >
                <i className="fas fa-chevron-left" />
              </button>
            )}
            {sortedPhotos.concat(videos)?.length &&
              currentSlide < sortedPhotos.concat(videos).length - 1 && (
                <button
                  type="button"
                  onClick={e => e.stopPropagation() || secondSlider.next() || slider.next()}
                  className="popup__slider-arrow-button popup__slider-arrow-button--right"
                >
                  <i className="fas fa-chevron-right" />
                </button>
              )}
          </>
        )}
      </div>
    </div>
  );
};

VehicleImagesPopupSlider.propTypes = {
  photos: PropTypes.any,
  videos: PropTypes.array,
  initial: PropTypes.number,
  onDamageClick: PropTypes.func,
  isDamageSlider: PropTypes.bool,
  showImageTitle: PropTypes.bool,
};

VehicleImagesPopupSlider.defaultProps = {
  photos: [],
  showImageTitle: true,
  videos: [],
  initial: 0,
  onDamageClick: () => {},
  isDamageSlider: false,
};

export default VehicleImagesPopupSlider;
